import type {
  PublicCampaignSchema,
  RaffleProgressSchema,
  RaffleTaskProgressSchema,
} from "@/types";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import type { Raffle, RaffleTask } from "./types";

/**
 * Join the two streams into the rendered `Raffle[]`. Pure — the store calls it (via `derive.ts`)
 * after either of them updates, and it must stay clock-free so the store's `listEqual`
 * short-circuit keeps working.
 *
 * **The type split.** This claims `type === "RF"` and nothing else. `"RA"` is Races — a
 * tournament-shaped type `modules/tournament` already claims — so the filter is an exact match on
 * `"RF"`, never a prefix or a `startsWith("R")`. `resolveCurrentCampaign` returns the FIRST module
 * whose `resolve` matches: a filter that also caught `"RA"` would render Races as raffles (or make
 * them vanish). Change the tournament and raffle filters together; `assemble.test.ts` pins it.
 *
 * A campaign is included as soon as its `campaigns` tile arrives, before the progress stream lands
 * — `total_tickets: null` means "not known yet", not "no tickets". Once the stream HAS landed
 * (`progressLanded`) a raffle with no row of its own is a raffle the player has not touched, which
 * is 0 tickets: the per-player stream renders a new raffle on its next tick, up to ~30 s after the
 * campaign appears, and the player must not stare at a placeholder until then.
 */
export function assembleRaffles(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  raffleProgressById: Record<CampaignKey, RaffleProgressSchema>,
  progressLanded = false
): Raffle[] {
  const result: Raffle[] = [];

  for (const campaign of Object.values(campaignsById)) {
    if (campaign.type !== "RF" || !campaign.raffle) continue;

    const progress = raffleProgressById[campaignKey(campaign)];
    const liveByTaskId = new Map<number, RaffleTaskProgressSchema>(
      (progress?.tasks ?? []).map((row) => [row.task_id, row])
    );

    const tasks: RaffleTask[] = [...campaign.raffle.tasks]
      .sort((a, b) => a.order - b.order || a.id - b.id)
      .map((task) => {
        const live = liveByTaskId.get(task.id);
        return {
          id: task.id,
          order: task.order,
          kind: task.kind,
          title: task.title,
          tickets_per_completion: task.tickets_per_completion,
          threshold: task.threshold,
          max_tickets: live?.max_tickets ?? task.max_tickets,
          tickets: live?.tickets ?? 0,
          progress: live?.progress ?? null,
        };
      });

    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;

    result.push({
      campaign,
      raffle: campaign.raffle,
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : { value: 0, currency: "" },
      prizes: [...(campaign.prizes ?? [])].sort(
        (a, b) => (a.place_from ?? 0) - (b.place_from ?? 0)
      ),
      tasks,
      total_tickets: progress
        ? progress.total_tickets
        : progressLanded
          ? 0
          : null,
      winners: campaign.raffle.winners ?? [],
      eligible_games: games,
      eligible_games_count: count,
    });
  }

  return result;
}
